import { useState, useEffect, useCallback } from 'react';
import { DEFAULT_PAYMENT_FILTERS } from '../components/kasabank/kasaBankHelpers';

/**
 * Custom hook that manages payment-type filters and row selection over an
 * already-paginated/filtered operations list (data filtering and pagination
 * happen on the backend - see useKasaBankData).
 *
 * @param {Array} operacje  Operations array for the current page
 * @returns {Object}        Filters state and actions
 */
export function useOperationFilters(operacje) {
  const [paymentFilters, setPaymentFilters] = useState({ ...DEFAULT_PAYMENT_FILTERS });
  const [filteredOperations, setFilteredOperations] = useState([]);
  const [selectedOperations, setSelectedOperations] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // ---------- payment-type filtering (client-side only) ----------
  useEffect(() => {
    if (!operacje || operacje.length === 0) {
      setFilteredOperations([]);
      return;
    }
    setFilteredOperations(operacje.filter((op) => paymentFilters[op.typ_platnosci] === true));
  }, [operacje, paymentFilters]);

  const togglePaymentFilter = useCallback((type) => {
    setPaymentFilters((prev) => ({ ...prev, [type]: !prev[type] }));
  }, []);

  const toggleAllFilters = useCallback((checked) => {
    setPaymentFilters({
      gotowka: checked,
      karta: checked,
      blik: checked,
      przelew: checked,
      kupon: checked
    });
  }, []);

  // ---------- row selection ----------
  const toggleOperationSelection = useCallback((operationId) => {
    setSelectedOperations((prev) => {
      const next = new Set(prev);
      if (next.has(operationId)) {
        next.delete(operationId);
      } else {
        next.add(operationId);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectAll) {
      setSelectedOperations(new Set());
    } else {
      setSelectedOperations(new Set(filteredOperations.map((op) => op.id)));
    }
    setSelectAll((prev) => !prev);
  }, [selectAll, filteredOperations]);

  // Sync "select all" checkbox with current selection
  useEffect(() => {
    if (filteredOperations.length === 0) {
      setSelectAll(false);
    } else {
      setSelectAll(filteredOperations.every((op) => selectedOperations.has(op.id)));
    }
  }, [selectedOperations, filteredOperations]);

  return {
    paymentFilters,
    filteredOperations,
    selectedOperations,
    selectAll,
    togglePaymentFilter,
    toggleAllFilters,
    toggleOperationSelection,
    toggleSelectAll
  };
}
